import React, { useState, useEffect } from 'react';
import { Bell, BellOff, X, Check } from 'lucide-react';
import { Button } from './ui/button';

const NotificationBanner = ({ onRequestPermission, permission, isSupported }) => {
  const [dismissed, setDismissed] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    // Check if user has already dismissed the banner
    const wasDismissed = localStorage.getItem('notification_banner_dismissed');
    if (wasDismissed) {
      setDismissed(true);
    }
  }, []);

  useEffect(() => {
    if (permission === 'granted') {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }
  }, [permission]);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('notification_banner_dismissed', 'true');
  };

  const handleEnable = async () => {
    const granted = await onRequestPermission();
    if (granted) {
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setDismissed(true);
      }, 3000);
    }
  };

  // Don't show if not supported, already granted, denied, or dismissed
  if (!isSupported || permission === 'granted' || permission === 'denied' || dismissed) {
    if (showSuccess) {
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center gap-3 animate-fade-in">
          <div className="bg-green-100 p-2 rounded-full">
            <Check className="w-5 h-5 text-green-600" />
          </div>
          <div className="flex-1">
            <p className="text-green-800 font-medium">¡Notificaciones activadas!</p>
            <p className="text-green-600 text-sm">Recibirás alertas cuando haya actualizaciones en tus paquetes.</p>
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex items-center gap-4" data-testid="notification-banner">
      <div className="bg-blue-100 p-3 rounded-full">
        <Bell className="w-6 h-6 text-blue-600" />
      </div>
      <div className="flex-1">
        <p className="text-blue-800 font-semibold">🔔 Activa las notificaciones</p>
        <p className="text-blue-600 text-sm">
          Recibe alertas instantáneas cuando tus paquetes lleguen al almacén o cambien de estado.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button
          onClick={handleEnable}
          className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
          data-testid="enable-notifications-btn"
        >
          <Bell className="w-4 h-4" />
          Activar
        </Button>
        <button
          onClick={handleDismiss}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          title="Cerrar"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// Compact notification toggle for settings
export const NotificationToggle = ({ permission, isSupported, onRequestPermission }) => {
  const [loading, setLoading] = useState(false);

  const handleToggle = async () => {
    if (permission === 'granted') {
      // Can't revoke programmatically, show instructions
      alert('Para desactivar las notificaciones, ve a la configuración de tu navegador.');
      return;
    }
    
    setLoading(true);
    await onRequestPermission();
    setLoading(false);
  };

  if (!isSupported) {
    return (
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-3">
          <BellOff className="w-5 h-5 text-gray-400" />
          <div>
            <p className="font-medium text-gray-600">Notificaciones no disponibles</p>
            <p className="text-sm text-gray-500">Tu navegador no soporta notificaciones push</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
      <div className="flex items-center gap-3">
        {permission === 'granted' ? (
          <div className="bg-green-100 p-2 rounded-full">
            <Bell className="w-5 h-5 text-green-600" />
          </div>
        ) : (
          <div className="bg-gray-100 p-2 rounded-full">
            <BellOff className="w-5 h-5 text-gray-400" />
          </div>
        )}
        <div>
          <p className="font-medium text-gray-800">Notificaciones Push</p>
          <p className="text-sm text-gray-500">
            {permission === 'granted' 
              ? 'Recibirás alertas de tus paquetes' 
              : permission === 'denied'
              ? 'Bloqueadas - Actívalas desde configuración del navegador'
              : 'Activa para recibir alertas instantáneas'}
          </p>
        </div>
      </div>
      <Button
        onClick={handleToggle}
        disabled={loading || permission === 'denied'}
        variant={permission === 'granted' ? 'outline' : 'default'}
        className={permission === 'granted' ? 'text-green-600 border-green-300' : ''}
      >
        {loading ? (
          <span className="animate-spin">⏳</span>
        ) : permission === 'granted' ? (
          <>
            <Check className="w-4 h-4 mr-1" />
            Activas
          </>
        ) : permission === 'denied' ? (
          'Bloqueadas'
        ) : (
          'Activar'
        )}
      </Button>
    </div>
  );
};

export default NotificationBanner;
